import React from 'react';
import { cx } from './styles';

/**
 * Tone-based pill. Absorbs the ~15 near-identical inline badge styles spread
 * across the v1 pages — status chips, link-state chips, platform chips.
 *
 * Violet is reserved for CrowdVolt: it already means "CrowdVolt" in this
 * product and must keep meaning that.
 */
export type BadgeTone = 'neutral' | 'blue' | 'green' | 'amber' | 'red' | 'violet';
export type BadgeSize = 'sm' | 'md';

export interface BadgeProps {
  tone?: BadgeTone;
  size?: BadgeSize;
  /** Render a 6px leading dot in the tone colour. */
  dot?: boolean;
  /** Monospace the label — for ids, masked phones, ticket types. */
  mono?: boolean;
  title?: string;
  children: React.ReactNode;
}

const toneStyles: Record<BadgeTone, string> = {
  neutral: 'bg-white/[0.06] text-white/60 border-white/10',
  blue: 'bg-[#3b82f6]/15 text-[#60a5fa] border-[#3b82f6]/25',
  green: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/25',
  amber: 'bg-amber-500/10 text-amber-400 border-amber-500/25',
  red: 'bg-red-500/10 text-red-400 border-red-500/25',
  violet: 'bg-violet-500/10 text-violet-300 border-violet-500/30',
};

const dotStyles: Record<BadgeTone, string> = {
  neutral: 'bg-white/40',
  blue: 'bg-[#60a5fa]',
  green: 'bg-emerald-400',
  amber: 'bg-amber-400',
  red: 'bg-red-400',
  violet: 'bg-violet-400',
};

const sizeStyles: Record<BadgeSize, string> = {
  sm: 'text-[10px] px-1.5 py-0.5 gap-1',
  md: 'text-[11px] px-2 py-0.5 gap-1.5',
};

export const Badge: React.FC<BadgeProps> = ({
  tone = 'neutral',
  size = 'md',
  dot,
  mono,
  title,
  children,
}) => (
  <span
    title={title}
    className={cx(
      'inline-flex items-center rounded-full border font-medium whitespace-nowrap',
      toneStyles[tone],
      sizeStyles[size],
      mono && 'font-mono',
    )}
  >
    {dot && <span className={cx('w-1.5 h-1.5 rounded-full flex-shrink-0', dotStyles[tone])} />}
    {children}
  </span>
);
